/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Common GLSL Shader Library
   Standardized color space, blend modes, matrix math, noise,
   and sampling routines for consistent shader behavior.
   ═══════════════════════════════════════════════════════════════ */

export const GLSL_HEADER = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 fragColor;
`;

export const GLSL_COLOR_UTILS = `
// Rec. 709 luminance weights
float getLuminance(vec3 color) {
  return dot(color, vec3(0.2126, 0.7152, 0.0722));
}

// RGB <-> HSV conversion
vec3 rgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}
`;

export const GLSL_NOISE_UTILS = `
// Fast procedural hash noise with reproducible seed
float hash21(vec2 p, float seed) {
  p = fract(p * vec2(123.34, 456.21) + vec2(seed * 17.13, seed * 31.41));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Procedural 2D Gaussian-like noise
vec3 hash23(vec2 p, float seed) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(443.897, 441.423, 437.195) + seed);
  p3 += dot(p3, p3.yzx + 19.19);
  return fract((p3.xxy + p3.yzz) * p3.zyx);
}
`;

export const GLSL_BAYER_MATRICES = `
// 4x4 Bayer Matrix (values normalized to 0..1)
float bayer4(vec2 coord) {
  int x = int(mod(coord.x, 4.0));
  int y = int(mod(coord.y, 4.0));
  int index = x + y * 4;
  
  // Normalized 4x4 threshold pattern
  const float m[16] = float[16](
     0.0 / 16.0,  8.0 / 16.0,  2.0 / 16.0, 10.0 / 16.0,
    12.0 / 16.0,  4.0 / 16.0, 14.0 / 16.0,  6.0 / 16.0,
     3.0 / 16.0, 11.0 / 16.0,  1.0 / 16.0,  9.0 / 16.0,
    15.0 / 16.0,  7.0 / 16.0, 13.0 / 16.0,  5.0 / 16.0
  );
  return m[index];
}

// 8x8 Bayer Matrix
float bayer8(vec2 coord) {
  vec2 b4_coord = mod(coord, 4.0);
  float b4 = bayer4(b4_coord);
  
  vec2 b2_coord = floor(mod(coord, 8.0) / 4.0);
  int idx = int(b2_coord.x + b2_coord.y * 2.0);
  const float m2[4] = float[4](0.0, 2.0, 3.0, 1.0);
  
  return (b4 * 4.0 + m2[idx] / 4.0) / 4.0;
}
`;

export const GLSL_BLEND_MODES = `
// Standard photoshop/svg compositing blend formulas on straight RGB
vec3 blendNormal(vec3 base, vec3 blend) {
  return blend;
}

vec3 blendMultiply(vec3 base, vec3 blend) {
  return base * blend;
}

vec3 blendScreen(vec3 base, vec3 blend) {
  return 1.0 - (1.0 - base) * (1.0 - blend);
}

vec3 blendOverlay(vec3 base, vec3 blend) {
  return vec3(
    base.r < 0.5 ? (2.0 * base.r * blend.r) : (1.0 - 2.0 * (1.0 - base.r) * (1.0 - blend.r)),
    base.g < 0.5 ? (2.0 * base.g * blend.g) : (1.0 - 2.0 * (1.0 - base.g) * (1.0 - blend.g)),
    base.b < 0.5 ? (2.0 * base.b * blend.b) : (1.0 - 2.0 * (1.0 - base.b) * (1.0 - blend.b))
  );
}

vec3 blendDarken(vec3 base, vec3 blend) {
  return min(base, blend);
}

vec3 blendLighten(vec3 base, vec3 blend) {
  return max(base, blend);
}

vec3 blendDifference(vec3 base, vec3 blend) {
  return abs(base - blend);
}
`;
